import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { isMac, isOpenShortcut } from "v2/utils/commandSearch";
import CommandPalette from "./CommandPalette";

/**
 * The navbar's search button. Opens the command palette, as does Cmd+K
 * (Ctrl+K on Windows and Linux) anywhere in the workspace.
 */
const SearchBar = ({ workspaceId }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const shortcut = isMac() ? "⌘K" : `${t("navigation.search.keys.ctrl")} K`;

  const close = useCallback(() => {
    setOpen(false);
    // Give focus back to the button that opened it.
    requestAnimationFrame(() => triggerRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!workspaceId) return undefined;
    const onKeyDown = (e) => {
      if (!isOpenShortcut(e)) return;
      e.preventDefault();
      setOpen((wasOpen) => !wasOpen);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [workspaceId]);

  if (!workspaceId) return null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("navigation.search.open")}
        aria-keyshortcuts={isMac() ? "Meta+K" : "Control+K"}
        className="group flex h-10 w-10 items-center justify-center gap-2.5 rounded-[10px] text-[#6B7280] outline-none transition duration-150 hover:bg-[#F5F5F5] focus-visible:ring-2 focus-visible:ring-[#E07A3A]/40 md:w-[260px] md:justify-start md:border md:border-[#EEEEF0] md:bg-[#F8F9FA] md:px-3 md:hover:border-[#E5E7EB] md:hover:bg-white md:hover:shadow-[0_1px_2px_rgba(17,24,39,0.05)]"
      >
        <Search
          size={18}
          className="shrink-0 transition-colors duration-150 group-hover:text-[#E07A3A]"
        />
        <span className="hidden flex-1 text-left text-sm text-[#9CA3AF] md:block">
          {t("navigation.search.trigger")}
        </span>
        <kbd className="hidden h-5 items-center rounded-md border border-[#E5E7EB] bg-white px-1.5 font-sans text-[11px] font-medium text-[#6B7280] shadow-[0_1px_0_rgba(17,24,39,0.06)] md:inline-flex">
          {shortcut}
        </kbd>
      </button>

      <CommandPalette open={open} onClose={close} workspaceId={workspaceId} />
    </>
  );
};

export default SearchBar;
